import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { errorToast, successToast } from "../../Toasts/Toasts";
import { filterAggregatorTransactions, getAggregatorTransactions } from "../../../services/supplierService";
import { aggregatorTransactionTypes } from "../../../utils/constants";

const TransactionFilter = ({ showTransactionFilter, setShowTransactionFilter, supplierId, setTransactions, setActiveFilters, activeFilters }) => {
  const [filterValues, setFilterValues] = useState({
    transaction_id: "",
    amount_usd: "",
    type: "",
    transactionDate: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (showTransactionFilter && activeFilters) {
      const transactionDate = activeFilters.startDate && activeFilters.endDate
        ? `${activeFilters.startDate} - ${activeFilters.endDate}`
        : "";
      setFilterValues({
        transaction_id: activeFilters.transaction_id || "",
        amount_usd: activeFilters.amount_usd || "",
        type: activeFilters.type || "",
        transactionDate,
      });
    }
  }, [showTransactionFilter]);

  const handleClose = () => {
    setShowTransactionFilter(false);
  };

  const handleFilterChange = (field, value) => {
    setFilterValues(prev => ({ ...prev, [field]: value }));
  };

  const handleDateRange = (event, picker) => {
    const dateRange = picker.startDate.format("YYYY-MM-DD") + " - " + picker.endDate.format("YYYY-MM-DD");
    handleFilterChange("transactionDate", dateRange);
  };

  const handleCancelDate = (event, picker) => {
    picker.element.val("");
    handleFilterChange("transactionDate", "");
  };

  const buildFilterPayload = () => {
    const payload = {};
    if (filterValues.transaction_id) payload.transaction_id = filterValues.transaction_id;
    if (filterValues.amount_usd) payload.amount_usd = filterValues.amount_usd;
    if (filterValues.type) payload.type = filterValues.type;
    if (filterValues.transactionDate) {
      const [startDate, endDate] = filterValues.transactionDate.split(" - ");
      payload.startDate = startDate.trim();
      payload.endDate = endDate.trim();
    }
    return payload;
  };

  const handleSubmit = async () => {
    const payload = buildFilterPayload();

    if (Object.keys(payload).length === 0) {
      errorToast("No field selected for filter");
      return;
    }

    try {
      setLoading(true);
      const data = await filterAggregatorTransactions(supplierId, payload);
      const result = Array.isArray(data) ? data
        : Array.isArray(data?.data) ? data.data
        : Array.isArray(data?.AggregatorTransactions) ? data.AggregatorTransactions
        : [];
      setTransactions(result);
      setActiveFilters(payload);
      successToast("Filter applied successfully");
      setShowTransactionFilter(false);
    } catch (error) {
      errorToast("Error occurred while filtering");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setFilterValues({ transaction_id: "", amount_usd: "", type: "", transactionDate: "" });
    setActiveFilters({});

    try {
      setLoading(true);
      const data = await getAggregatorTransactions(supplierId);
      const result = Array.isArray(data) ? data
        : Array.isArray(data?.data) ? data.data
        : Array.isArray(data?.AggregatorTransactions) ? data.AggregatorTransactions
        : [];
      setTransactions(result);
    } catch (error) {
      errorToast("Error resetting transactions");
    } finally {
      setLoading(false);
      setShowTransactionFilter(false);
    }
  };

  return (
    <Modal show={showTransactionFilter} size="md" centered enforceFocus={false}>
      <div className="modal-simple modal-edit-user">
        <div className="modal-content p-3 p-md-3">
          <Modal.Body className="p-2">
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
            />
            <div className="text-left mb-0">
              <h3 className="mb-3">Filter</h3>
            </div>

            <div className="row">
              <div className="col-lg-6 mb-3">
                <label className="form-label">Transaction ID</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder=""
                  value={filterValues.transaction_id}
                  onChange={(e) => handleFilterChange("transaction_id", e.target.value)}
                />
              </div>

              <div className="col-lg-6 mb-3">
                <label className="form-label">
                  Amount <small>(USD)</small>
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder=""
                  value={filterValues.amount_usd}
                  onChange={(e) => handleFilterChange("amount_usd", e.target.value)}
                />
              </div>

              <div className="col-lg-6 mb-3">
                <label className="form-label">Type Of Transaction</label>
                <select
                  className="form-control form-select"
                  value={filterValues.type}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
                >
                  <option value="">...</option>
                  {aggregatorTransactionTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-lg-6 mb-3">
                <label className="form-label">Transaction Date</label>
                <DateRangePicker
                  initialSettings={{
                    autoUpdateInput: false,
                    locale: { cancelLabel: "Clear", format: "YYYY-MM-DD" },
                    showDropdowns: true,
                    drops: "up",
                  }}
                  onApply={handleDateRange}
                  onCancel={handleCancelDate}
                >
                  <input
                    type="text"
                    className="form-control flatpickr-input active"
                    placeholder="YYYY-MM-DD to YYYY-MM-DD"
                    value={filterValues.transactionDate}
                    readOnly
                  />
                </DateRangePicker>
              </div>
            </div>

            <div className="row">
              <div className="col-12 text-center mt-4">
                <button
                  type="button"
                  className="btn btn-primary rounded-pill me-sm-3 me-1 waves-effect waves-light show-filter"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Applying..." : "Submit"}
                </button>
                <button
                  type="button"
                  className="btn btn-label-secondary rounded-pill btn-reset waves-effect"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal.Body>
        </div>
      </div>
    </Modal>
  );
};

export default TransactionFilter;
